import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  ListSubheader,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  AccountTree,
  Save,
  Publish,
  ContentCopy,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import WorkflowDiagram, {
  DIAGRAM_END,
  diagramWorkflowId,
  diagramWorkflowTarget,
} from "../components/WorkflowDiagram";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  workflowsService,
  workflowErrorMessage,
  WorkflowDueAnchor,
  WorkflowStep,
  WorkflowStepKind,
  WorkflowListItem,
  WorkflowProfileOption,
  WorkflowTransition,
  WorkflowTransitionInput,
  WorkflowVersionDetail,
  CHECKER_OPTIONS,
  DUE_ANCHOR_LABELS,
  DUE_UNIT_LABELS,
  WorkflowDueUnit,
  STEP_KIND_LABELS,
  TARGET_KIND_LABELS,
  VERSION_STATUS_COLORS,
  VERSION_STATUS_LABELS,
} from "../services/workflowsService";

const MAX_STEPS = 40;
const MAX_DUE_AMOUNT = 1000;

/** Destino "encerra o processo" nos selects de ligação (toStepKey null no payload). */
const END = DIAGRAM_END;

// Nome de fluxo é livre e pode ser longo: o menu não pode passar da largura da tela no celular.
const TARGET_MENU_PROPS = { PaperProps: { sx: { maxWidth: { xs: "88vw", sm: 420 } } } };

// Referência interna por uid (não persistido): renomear a chave ou reordenar as etapas não
// quebra as ligações em edição — a conversão para step_key acontece só ao montar o payload.
let uidSequence = 0;
const nextUid = () => `s${++uidSequence}`;

interface OptionForm {
  uid: string;
  optionKey: string;
  label: string;
  keyEdited: boolean;
  target: string;
}

interface StepForm {
  uid: string;
  stepKey: string;
  keyEdited: boolean;
  kind: WorkflowStepKind;
  name: string;
  description: string;
  checker: string;
  dueAnchor: WorkflowDueAnchor;
  dueAmount: string;
  dueUnit: WorkflowDueUnit;
  responsibleProfileId: string | null;
  required: boolean;
  next: string;
  options: OptionForm[];
}

const slugify = (value: string, max: number): string =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, max);

const blankOption = (): OptionForm => ({
  uid: nextUid(),
  optionKey: "",
  label: "",
  keyEdited: false,
  target: END,
});

const blankStep = (): StepForm => ({
  uid: nextUid(),
  stepKey: "",
  keyEdited: false,
  kind: "activity",
  name: "",
  description: "",
  checker: "",
  dueAnchor: "none",
  dueAmount: "",
  dueUnit: "days",
  responsibleProfileId: null,
  required: true,
  next: END,
  options: [],
});

/** Converte o desenho vindo da API para o formulário, resolvendo as ligações por step_key. */
const toForm = (detail: WorkflowVersionDetail): StepForm[] => {
  const ordered = [...detail.steps].sort((a, b) => a.order - b.order);
  const uidByKey = new Map<string, string>();
  const steps: StepForm[] = ordered.map((step) => {
    const uid = nextUid();
    uidByKey.set(step.stepKey, uid);
    return {
      uid,
      stepKey: step.stepKey,
      keyEdited: true,
      kind: step.kind,
      name: step.name,
      description: step.description || "",
      checker: step.checker || "",
      dueAnchor: step.dueAnchor,
      dueAmount:
        step.dueAmount === null || step.dueAmount === undefined ? "" : String(step.dueAmount),
      dueUnit: step.dueUnit ?? "days",
      responsibleProfileId: step.responsibleProfileId,
      required: step.required,
      next: END,
      options: [...step.options]
        .sort((a, b) => a.order - b.order)
        .map((option) => ({
          uid: nextUid(),
          optionKey: option.optionKey,
          label: option.label,
          keyEdited: true,
          target: END,
        })),
    };
  });

  const resolve = (transition: WorkflowTransition): string => {
    if (transition.toWorkflowId) return diagramWorkflowTarget(transition.toWorkflowId);
    return transition.toStepKey && uidByKey.has(transition.toStepKey)
      ? uidByKey.get(transition.toStepKey)!
      : END;
  };

  detail.transitions.forEach((transition) => {
    const step = steps.find((s) => s.stepKey === transition.fromStepKey);
    if (!step) return;
    if (transition.optionKey) {
      const option = step.options.find((o) => o.optionKey === transition.optionKey);
      if (option) option.target = resolve(transition);
      return;
    }
    step.next = resolve(transition);
  });

  return steps;
};

interface PendingAction {
  title: string;
  message: string;
  confirmText: string;
  run: () => Promise<void>;
}

const WorkflowEditor: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  // A versão em edição vive no estado (e não só na URL): ao clonar, a mesma página passa a
  // apontar para o rascunho novo sem remontar o componente.
  const [versionId, setVersionId] = useState(getParam("versionId") || "");

  const [detail, setDetail] = useState<WorkflowVersionDetail | null>(null);
  const [steps, setSteps] = useState<StepForm[]>([]);
  const [profiles, setProfiles] = useState<WorkflowProfileOption[]>([]);
  const [otherWorkflows, setOtherWorkflows] = useState<WorkflowListItem[]>([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const readOnly = detail !== null && detail.status !== "draft";

  const load = async () => {
    if (!versionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [data, profileOptions] = await Promise.all([
        workflowsService.getVersion(versionId),
        workflowsService.getProfiles(),
      ]);
      setDetail(data);
      setSteps(toForm(data));
      setProfiles(profileOptions);
      setNotes(data.notes || "");
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao carregar a versão do fluxo."), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Desenho do fluxo";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  // Fora do load() de propósito: a entidade chega depois da montagem e recarregar o desenho aqui
  // descartaria o que já estivesse sendo editado.
  useEffect(() => {
    const entityId = user?.entityId;
    if (!entityId) return;
    workflowsService
      .getByEntity(entityId, false)
      .then(setOtherWorkflows)
      .catch((e) =>
        showToast(workflowErrorMessage(e, "Erro ao carregar os fluxos de destino."), "error")
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.entityId]);

  const goToList = () => navigateTo("workflows");

  // --- edição das etapas ---
  const patchStep = (uid: string, changes: Partial<StepForm>) =>
    setSteps((prev) => prev.map((s) => (s.uid === uid ? { ...s, ...changes } : s)));

  const changeName = (uid: string, name: string) =>
    setSteps((prev) =>
      prev.map((s) =>
        s.uid === uid
          ? { ...s, name, stepKey: s.keyEdited ? s.stepKey : slugify(name, 60) }
          : s
      )
    );

  const changeKind = (uid: string, kind: WorkflowStepKind) =>
    setSteps((prev) =>
      prev.map((s) => {
        if (s.uid !== uid) return s;
        const options =
          kind === "decision" && s.options.length < 2
            ? [...s.options, ...Array.from({ length: 2 - s.options.length }, blankOption)]
            : s.options;
        return { ...s, kind, checker: kind === "activity" ? s.checker : "", options };
      })
    );

  const addStep = () =>
    setSteps((prev) => {
      if (prev.length >= MAX_STEPS) return prev;
      const created = blankStep();
      // Encadeia automaticamente: a última etapa que terminava o processo passa a apontar para a nova.
      const last = prev[prev.length - 1];
      const chained =
        last && last.kind !== "decision" && last.next === END
          ? prev.map((s) => (s.uid === last.uid ? { ...s, next: created.uid } : s))
          : prev;
      return [...chained, created];
    });

  const removeStep = (uid: string) =>
    setSteps((prev) =>
      prev
        .filter((s) => s.uid !== uid)
        .map((s) => ({
          ...s,
          next: s.next === uid ? END : s.next,
          options: s.options.map((o) => (o.target === uid ? { ...o, target: END } : o)),
        }))
    );

  const moveStep = (index: number, direction: -1 | 1) =>
    setSteps((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  // --- opções da decisão ---
  const patchOption = (stepUid: string, optionUid: string, changes: Partial<OptionForm>) =>
    setSteps((prev) =>
      prev.map((s) =>
        s.uid === stepUid
          ? { ...s, options: s.options.map((o) => (o.uid === optionUid ? { ...o, ...changes } : o)) }
          : s
      )
    );

  const changeOptionLabel = (stepUid: string, optionUid: string, label: string) =>
    setSteps((prev) =>
      prev.map((s) =>
        s.uid === stepUid
          ? {
              ...s,
              options: s.options.map((o) =>
                o.uid === optionUid
                  ? { ...o, label, optionKey: o.keyEdited ? o.optionKey : slugify(label, 60) }
                  : o
              ),
            }
          : s
      )
    );

  const addOption = (stepUid: string) =>
    setSteps((prev) =>
      prev.map((s) => (s.uid === stepUid ? { ...s, options: [...s.options, blankOption()] } : s))
    );

  const removeOption = (stepUid: string, optionUid: string) =>
    setSteps((prev) =>
      prev.map((s) =>
        s.uid === stepUid ? { ...s, options: s.options.filter((o) => o.uid !== optionUid) } : s
      )
    );

  // --- payload ---
  const buildPayload = (): { steps: WorkflowStep[]; transitions: WorkflowTransitionInput[] } => {
    const keyByUid = new Map(steps.map((s) => [s.uid, s.stepKey.trim()]));
    /** Os dois destinos nunca vão juntos: o servidor recusa etapa e fluxo preenchidos. */
    const destination = (target: string): { toStepKey: string | null; toWorkflowId: string | null } => {
      const workflowId = diagramWorkflowId(target);
      if (workflowId) return { toStepKey: null, toWorkflowId: workflowId };
      return { toStepKey: target === END ? null : keyByUid.get(target) ?? null, toWorkflowId: null };
    };

    const payloadSteps: WorkflowStep[] = steps.map((step, index) => ({
      stepKey: step.stepKey.trim(),
      kind: step.kind,
      name: step.name.trim(),
      description: step.description.trim() || null,
      checker: step.kind === "activity" && step.checker ? step.checker : null,
      dueAnchor: step.dueAnchor,
      dueAmount: step.dueAnchor === "none" ? null : Number(step.dueAmount),
      dueUnit: step.dueUnit,
      responsibleProfileId: step.responsibleProfileId || null,
      required: step.required,
      order: index,
      options:
        step.kind === "decision"
          ? step.options.map((option, optionIndex) => ({
              optionKey: option.optionKey.trim(),
              label: option.label.trim(),
              order: optionIndex,
            }))
          : [],
    }));

    const transitions: WorkflowTransitionInput[] = [];
    steps.forEach((step) => {
      const fromStepKey = step.stepKey.trim();
      if (step.kind === "decision") {
        step.options.forEach((option) =>
          transitions.push({
            fromStepKey,
            ...destination(option.target),
            optionKey: option.optionKey.trim(),
          })
        );
        return;
      }
      transitions.push({ fromStepKey, ...destination(step.next), optionKey: null });
    });

    return { steps: payloadSteps, transitions };
  };

  /** Espelha as regras do rascunho no servidor para dar mensagem apontando a etapa. */
  const validate = (): string | null => {
    const keys = new Set<string>();
    for (let i = 0; i < steps.length; i += 1) {
      const step = steps[i];
      const position = `Etapa ${i + 1}`;
      if (!step.name.trim()) return `${position}: informe o nome.`;
      if (!step.stepKey.trim()) return `${position}: informe a chave.`;
      const key = step.stepKey.trim().toLowerCase();
      if (keys.has(key)) return `${position}: a chave "${step.stepKey.trim()}" já é usada por outra etapa.`;
      keys.add(key);

      if (step.dueAnchor !== "none") {
        const amount = Number(step.dueAmount);
        if (!step.dueAmount.trim() || !Number.isInteger(amount) || amount < 1 || amount > MAX_DUE_AMOUNT) {
          return `${position}: informe o prazo (1 a ${MAX_DUE_AMOUNT}) ou escolha "Sem prazo".`;
        }
      }

      if (step.kind === "decision") {
        if (step.options.length < 2) return `${position}: uma decisão precisa de pelo menos 2 opções.`;
        const optionKeys = new Set<string>();
        for (const option of step.options) {
          if (!option.label.trim()) return `${position}: informe o rótulo de todas as opções.`;
          if (!option.optionKey.trim()) return `${position}: informe a chave de todas as opções.`;
          const optionKey = option.optionKey.trim().toLowerCase();
          if (optionKeys.has(optionKey)) {
            return `${position}: a chave de opção "${option.optionKey.trim()}" está repetida.`;
          }
          optionKeys.add(optionKey);
        }
      }
    }
    return null;
  };

  const persist = async (): Promise<boolean> => {
    const error = validate();
    if (error) {
      showToast(error, "error");
      return false;
    }
    const payload = buildPayload();
    await workflowsService.saveDesign(versionId, {
      notes: notes.trim() || null,
      steps: payload.steps,
      transitions: payload.transitions,
    });
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      if (await persist()) {
        showToast("Rascunho salvo.", "success");
        await load();
      }
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao salvar o rascunho."), "error");
    } finally {
      setSaving(false);
    }
  };

  // Publicar sempre salva antes: publicar um desenho diferente do que está na tela seria mentira.
  const publish = async () => {
    setSaving(true);
    try {
      if (!(await persist())) return;
      await workflowsService.publishVersion(versionId);
      showToast("Versão publicada. A partir de agora ela é imutável.", "success");
      await load();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao publicar a versão."), "error");
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    setSaving(true);
    try {
      await workflowsService.removeVersion(versionId);
      showToast("Rascunho descartado.", "success");
      goToList();
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao descartar o rascunho."), "error");
    } finally {
      setSaving(false);
    }
  };

  const cloneToNewVersion = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const newVersionId = await workflowsService.createVersion(detail.workflowId, detail.id, null);
      navigateTo("workflow-editor", { versionId: newVersionId });
      setVersionId(newVersionId);
      showToast("Nova versão criada em rascunho.", "success");
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao criar a nova versão."), "error");
    } finally {
      setSaving(false);
    }
  };

  // O modal fica aberto durante a execução para o overlay "Processando" do ConfirmModal aparecer.
  const runPendingAction = async () => {
    if (!pendingAction) return;
    await pendingAction.run();
    setPendingAction(null);
  };

  const stepChoices = useMemo(
    () => steps.map((step, index) => ({ uid: step.uid, label: `${index + 1}. ${step.name || "(sem nome)"}` })),
    [steps]
  );

  /** O próprio fluxo fica de fora: apontar para si mesmo é recusado pelo servidor. */
  const workflowChoices = useMemo(
    () =>
      otherWorkflows
        .filter((workflow) => workflow.id !== detail?.workflowId)
        .map((workflow) => ({ value: diagramWorkflowTarget(workflow.id), name: workflow.name })),
    [otherWorkflows, detail]
  );

  // Nome por id vindo das duas pontas: a lista de fluxos e o que o servidor já resolveu no desenho
  // (é o que sobra quando o fluxo de destino saiu do ar e não vem mais na lista).
  const workflowNameById = useMemo(() => {
    const names: Record<string, string> = {};
    (detail?.transitions ?? []).forEach((transition) => {
      if (transition.toWorkflowId && transition.toWorkflowName) {
        names[transition.toWorkflowId] = transition.toWorkflowName;
      }
    });
    otherWorkflows.forEach((workflow) => {
      names[workflow.id] = workflow.name;
    });
    return names;
  }, [detail, otherWorkflows]);

  /** Itens dos selects de destino, em grupos: etapas, encerrar e outros fluxos. */
  const renderTargetItems = (current: string): React.ReactNode[] => {
    const items: React.ReactNode[] = [];
    const itemSx = { whiteSpace: "normal" };
    if (stepChoices.length > 0) {
      items.push(<ListSubheader key="group-steps">Etapas deste fluxo</ListSubheader>);
      stepChoices.forEach((choice) =>
        items.push(
          <MenuItem key={choice.uid} value={choice.uid} sx={itemSx}>
            {choice.label}
          </MenuItem>
        )
      );
    }
    items.push(<ListSubheader key="group-end">Encerrar</ListSubheader>);
    items.push(
      <MenuItem key={END} value={END} sx={itemSx}>
        Encerrar o processo
      </MenuItem>
    );
    if (workflowChoices.length > 0) {
      items.push(<ListSubheader key="group-workflows">Ir para outro fluxo</ListSubheader>);
      workflowChoices.forEach((choice) =>
        items.push(
          <MenuItem key={choice.value} value={choice.value} sx={itemSx}>
            {choice.name}
          </MenuItem>
        )
      );
    }
    // Fluxo de destino que saiu do ar continua selecionado: trocar o desenho calado seria pior.
    const currentWorkflowId = diagramWorkflowId(current);
    if (currentWorkflowId && !workflowChoices.some((choice) => choice.value === current)) {
      items.push(
        <MenuItem key={current} value={current} sx={itemSx}>
          {workflowNameById[currentWorkflowId] ?? "Outro fluxo (indisponível)"}
        </MenuItem>
      );
    }
    return items;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
              <IconButton
                onClick={goToList}
                title="Voltar para Fluxos de trabalho"
                sx={{ mt: "-4px" }}
              >
                <ArrowBack />
              </IconButton>
              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <Typography
                  variant="h4"
                  className="c4u-page-header__title"
                  sx={{ wordBreak: "break-word" }}
                >
                  {detail?.workflowName || "Desenho do fluxo"}
                </Typography>
                {detail && (
                  <>
                    <Chip
                      label={`v${detail.versionNumber}`}
                      size="small"
                      sx={{ height: 22, fontSize: "0.7rem", color: "#fff", bgcolor: colors.primary }}
                    />
                    <Chip
                      label={VERSION_STATUS_LABELS[detail.status] ?? detail.status}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        color: "#fff",
                        bgcolor: VERSION_STATUS_COLORS[detail.status] ?? "#6c757d",
                      }}
                    />
                    <Chip
                      label={TARGET_KIND_LABELS[detail.targetKind] ?? detail.targetKind}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  </>
                )}
              </Box>
              <Typography
                variant="body2"
                className="c4u-page-header__subtitle"
              >
                Etapas do processo e para onde cada uma leva. As ligações usam a chave da etapa, que é o que
                mantém a comparação entre versões de pé.
              </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                justifyContent: "flex-end",
              }}
            >
              <FaqButton />
            </Box>
          </Box>

          {/* Tudo — aviso, formulário e diagrama — dentro do mesmo painel do título */}
          <div className="c4u-panel">

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : !detail ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <AccountTree sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                Versão não encontrada
              </Typography>
              <Typography variant="body2">Volte à lista e abra o fluxo novamente.</Typography>
            </Box>
          ) : (
            <>
              {readOnly && (
                <Alert
                  severity="info"
                  // No celular a ação desce para a própria linha; sem isto ela espremia o texto
                  // numa coluna de uma palavra por linha
                  sx={{
                    mb: 2,
                    alignItems: "center",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    "& .MuiAlert-message": { flex: "1 1 220px", minWidth: 0 },
                    "& .MuiAlert-action": { pt: 0, pl: { xs: 0, sm: 2 }, ml: { xs: "auto", sm: 0 } },
                  }}
                  action={
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={cloneToNewVersion}
                      disabled={saving}
                      sx={{ textTransform: "none", color: colors.primary, whiteSpace: "nowrap" }}
                    >
                      Nova versão a partir desta
                    </Button>
                  }
                >
                  Versão publicada é imutável. Crie uma nova versão para alterar.
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #e9ecef", borderRadius: "8px" }}>
                    <TextField
                      label="Observações da versão"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      disabled={readOnly}
                      inputProps={{ maxLength: 500 }}
                      InputLabelProps={{ shrink: true }}
                      placeholder="O que muda nesta versão em relação à anterior."
                    />
                  </Paper>

                  {steps.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 6,
                        color: colors.textSecondary,
                        border: "1px dashed #ced4da",
                        borderRadius: "8px",
                      }}
                    >
                      <AccountTree sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                      <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                        Nenhuma etapa desenhada
                      </Typography>
                      <Typography variant="body2">
                        {readOnly
                          ? "Esta versão foi publicada sem etapas."
                          : 'Comece em "Adicionar etapa" para montar o processo.'}
                      </Typography>
                    </Box>
                  ) : (
                    steps.map((step, index) => (
                      <Paper
                        key={step.uid}
                        elevation={0}
                        sx={{
                          border: "1px solid #e9ecef",
                          borderRadius: "8px",
                          p: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                          <Chip
                            label={`Etapa ${index + 1}`}
                            size="small"
                            sx={{ bgcolor: colors.primary, color: "white", fontWeight: 600 }}
                          />
                          <Chip
                            label={STEP_KIND_LABELS[step.kind]}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.7rem" }}
                          />
                          <Box sx={{ flex: 1 }} />
                          {!readOnly && (
                            <>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => moveStep(index, -1)}
                                title="Subir"
                              >
                                <ArrowUpward sx={{ fontSize: "1.1rem" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={index === steps.length - 1}
                                onClick={() => moveStep(index, 1)}
                                title="Descer"
                              >
                                <ArrowDownward sx={{ fontSize: "1.1rem" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => removeStep(step.uid)}
                                title="Remover etapa"
                                sx={{ color: colors.error }}
                              >
                                <DeleteOutline sx={{ fontSize: "1.1rem" }} />
                              </IconButton>
                            </>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          <TextField
                            label="Nome da etapa"
                            required
                            value={step.name}
                            onChange={(e) => changeName(step.uid, e.target.value)}
                            size="small"
                            disabled={readOnly}
                            sx={{ flex: "1 1 240px", minWidth: 0 }}
                            inputProps={{ maxLength: 150 }}
                            InputLabelProps={{ shrink: true }}
                            placeholder="ex.: Avaliação inicial"
                          />
                          <TextField
                            label="Chave da etapa"
                            required
                            value={step.stepKey}
                            onChange={(e) =>
                              patchStep(step.uid, { stepKey: e.target.value, keyEdited: true })
                            }
                            size="small"
                            disabled={readOnly}
                            sx={{ flex: "1 1 200px", minWidth: 0 }}
                            inputProps={{ maxLength: 60 }}
                            InputLabelProps={{ shrink: true }}
                            helperText="Gerada do nome. Mudar a chave quebra a comparação histórica entre versões."
                          />
                        </Box>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          <FormControl size="small" sx={{ flex: "1 1 180px", minWidth: 0 }} disabled={readOnly}>
                            <InputLabel shrink>Tipo</InputLabel>
                            <Select
                              label="Tipo"
                              value={step.kind}
                              onChange={(e) => changeKind(step.uid, e.target.value as WorkflowStepKind)}
                              notched
                            >
                              {(Object.keys(STEP_KIND_LABELS) as WorkflowStepKind[]).map((k) => (
                                <MenuItem key={k} value={k}>
                                  {STEP_KIND_LABELS[k]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {step.kind === "activity" && (
                            <FormControl size="small" sx={{ flex: "1 1 260px", minWidth: 0 }} disabled={readOnly}>
                              <InputLabel shrink>Verificador</InputLabel>
                              <Select
                                label="Verificador"
                                value={step.checker}
                                onChange={(e) => patchStep(step.uid, { checker: e.target.value })}
                                notched
                                displayEmpty
                              >
                                {CHECKER_OPTIONS.map((option) => (
                                  <MenuItem key={option.value || "manual"} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                          <FormControl size="small" sx={{ flex: "1 1 220px", minWidth: 0 }} disabled={readOnly}>
                            <InputLabel shrink>Responsável</InputLabel>
                            <Select
                              label="Responsável"
                              value={profiles.some((p) => p.id === step.responsibleProfileId)
                                ? step.responsibleProfileId
                                : ""}
                              onChange={(e) => patchStep(step.uid, { responsibleProfileId: e.target.value })}
                              notched
                              displayEmpty
                            >
                              <MenuItem value="">Sem responsável definido</MenuItem>
                              {profiles.map((profile) => (
                                <MenuItem key={profile.id} value={profile.id}>
                                  {profile.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        <TextField
                          label="Descrição"
                          value={step.description}
                          onChange={(e) => patchStep(step.uid, { description: e.target.value })}
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          disabled={readOnly}
                          inputProps={{ maxLength: 500 }}
                          InputLabelProps={{ shrink: true }}
                          placeholder="O que precisa acontecer nesta etapa."
                        />

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                          <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 0 }} disabled={readOnly}>
                            <InputLabel shrink>Prazo a partir de</InputLabel>
                            <Select
                              label="Prazo a partir de"
                              value={step.dueAnchor}
                              onChange={(e) =>
                                patchStep(step.uid, {
                                  dueAnchor: e.target.value as WorkflowDueAnchor,
                                  dueAmount: e.target.value === "none" ? "" : step.dueAmount,
                                })
                              }
                              notched
                            >
                              {(Object.keys(DUE_ANCHOR_LABELS) as WorkflowDueAnchor[]).map((a) => (
                                <MenuItem key={a} value={a}>
                                  {DUE_ANCHOR_LABELS[a]}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {step.dueAnchor !== "none" && (
                            <>
                              <TextField
                                label="Prazo"
                                type="number"
                                value={step.dueAmount}
                                onChange={(e) => patchStep(step.uid, { dueAmount: e.target.value })}
                                size="small"
                                disabled={readOnly}
                                sx={{ flex: "1 1 110px", minWidth: 0 }}
                                inputProps={{ maxLength: 4, min: 1, max: MAX_DUE_AMOUNT }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <FormControl
                                size="small"
                                sx={{ flex: "1 1 150px", minWidth: 0 }}
                                disabled={readOnly}
                              >
                                <InputLabel shrink>Unidade</InputLabel>
                                <Select
                                  label="Unidade"
                                  value={step.dueUnit}
                                  onChange={(e) =>
                                    patchStep(step.uid, { dueUnit: e.target.value as WorkflowDueUnit })
                                  }
                                  notched
                                >
                                  {(Object.keys(DUE_UNIT_LABELS) as WorkflowDueUnit[]).map((u) => (
                                    <MenuItem key={u} value={u}>
                                      {DUE_UNIT_LABELS[u]}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </>
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={step.required}
                                disabled={readOnly}
                                onChange={(e) => patchStep(step.uid, { required: e.target.checked })}
                                sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }}
                              />
                            }
                            label="Obrigatória"
                          />
                        </Box>

                        <Divider />

                        {step.kind === "decision" ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              Opções da decisão (mínimo 2). Cada opção precisa de um destino próprio.
                            </Typography>
                            {step.options.map((option, optionIndex) => (
                              <Box
                                key={option.uid}
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  alignItems: "flex-start",
                                  border: "1px solid #e9ecef",
                                  borderRadius: "8px",
                                  p: 1,
                                }}
                              >
                                <TextField
                                  label={`Rótulo da opção ${optionIndex + 1}`}
                                  value={option.label}
                                  onChange={(e) => changeOptionLabel(step.uid, option.uid, e.target.value)}
                                  size="small"
                                  disabled={readOnly}
                                  sx={{ flex: "1 1 180px", minWidth: 0 }}
                                  inputProps={{ maxLength: 120 }}
                                  InputLabelProps={{ shrink: true }}
                                  placeholder="ex.: Segue em terapia"
                                />
                                <TextField
                                  label="Chave"
                                  value={option.optionKey}
                                  onChange={(e) =>
                                    patchOption(step.uid, option.uid, {
                                      optionKey: e.target.value,
                                      keyEdited: true,
                                    })
                                  }
                                  size="small"
                                  disabled={readOnly}
                                  sx={{ flex: "1 1 140px", minWidth: 0 }}
                                  inputProps={{ maxLength: 60 }}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <FormControl
                                  size="small"
                                  sx={{ flex: "1 1 180px", minWidth: 0 }}
                                  disabled={readOnly}
                                >
                                  <InputLabel shrink>Destino</InputLabel>
                                  <Select
                                    label="Destino"
                                    value={option.target}
                                    onChange={(e) =>
                                      patchOption(step.uid, option.uid, { target: e.target.value })
                                    }
                                    notched
                                    MenuProps={TARGET_MENU_PROPS}
                                  >
                                    {renderTargetItems(option.target)}
                                  </Select>
                                </FormControl>
                                {!readOnly && (
                                  <IconButton
                                    size="small"
                                    onClick={() => removeOption(step.uid, option.uid)}
                                    title="Remover opção"
                                    sx={{ color: colors.error, flexShrink: 0 }}
                                  >
                                    <DeleteOutline sx={{ fontSize: "1.1rem" }} />
                                  </IconButton>
                                )}
                              </Box>
                            ))}
                            {!readOnly && (
                              <Button
                                startIcon={<Add />}
                                onClick={() => addOption(step.uid)}
                                variant="outlined"
                                size="small"
                                sx={{
                                  textTransform: "none",
                                  color: colors.primary,
                                  borderColor: colors.primary,
                                  alignSelf: "flex-start",
                                }}
                              >
                                Adicionar opção
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <FormControl size="small" fullWidth disabled={readOnly}>
                            <InputLabel shrink>Próxima etapa</InputLabel>
                            <Select
                              label="Próxima etapa"
                              value={step.next}
                              onChange={(e) => patchStep(step.uid, { next: e.target.value })}
                              notched
                              MenuProps={TARGET_MENU_PROPS}
                            >
                              {renderTargetItems(step.next)}
                            </Select>
                          </FormControl>
                        )}
                      </Paper>
                    ))
                  )}

                  {!readOnly && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      <Button
                        startIcon={<Add />}
                        onClick={addStep}
                        disabled={steps.length >= MAX_STEPS}
                        variant="outlined"
                        sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary }}
                      >
                        Adicionar etapa
                      </Button>
                      {steps.length >= MAX_STEPS && (
                        <Typography variant="caption" sx={{ color: colors.textSecondary, alignSelf: "center" }}>
                          Máximo de {MAX_STEPS} etapas.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    width: { xs: "100%", lg: 480 },
                    flexShrink: 0,
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    p: 1.5,
                    position: { lg: "sticky" },
                    top: { lg: "100px" },
                    // Rolagem própria: sem ela só as etapas rolavam e o desenho de um fluxo longo
                    // ficava preso fora da tela enquanto se edita.
                    maxHeight: { lg: "calc(100vh - 120px)" },
                    overflowY: { lg: "auto" },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary, mb: 0.5 }}>Diagrama</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mb: 1 }}>
                    Atualiza conforme você edita.
                  </Typography>
                  <WorkflowDiagram
                    steps={steps}
                    workflowNames={workflowNameById}
                    fit
                    showLegend
                    expandable
                    title={detail?.workflowName}
                  />
                </Paper>
              </Box>

              {!readOnly && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    p: 1.5,
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    onClick={() =>
                      setPendingAction({
                        title: "Descartar rascunho",
                        message: `Descartar a v${detail.versionNumber}? O desenho em edição é perdido.`,
                        confirmText: "Descartar",
                        run: discard,
                      })
                    }
                    variant="outlined"
                    disabled={saving}
                    sx={{ textTransform: "none", color: colors.error, borderColor: colors.error }}
                  >
                    Descartar rascunho
                  </Button>
                  <Button
                    startIcon={<Save />}
                    onClick={save}
                    variant="outlined"
                    disabled={saving}
                    sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary }}
                  >
                    {saving ? "Salvando..." : "Salvar rascunho"}
                  </Button>
                  <Button
                    startIcon={<Publish />}
                    onClick={() =>
                      setPendingAction({
                        title: "Publicar versão",
                        message: `Publicar a v${detail.versionNumber}? O rascunho é salvo antes e, publicado, o desenho fica imutável — para mudar, será preciso criar uma nova versão.`,
                        confirmText: "Publicar",
                        run: publish,
                      })
                    }
                    variant="contained"
                    disabled={saving}
                    sx={{ textTransform: "none", backgroundColor: colors.primary }}
                  >
                    Publicar
                  </Button>
                </Paper>
              )}
            </>
          )}
          </div>
        </Container>
      </Box>

      <ConfirmModal
        isOpen={pendingAction !== null}
        title={pendingAction?.title ?? ""}
        message={pendingAction?.message ?? ""}
        type="warning"
        confirmButtonText={pendingAction?.confirmText ?? "Confirmar"}
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={runPendingAction}
        onCancel={() => setPendingAction(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default WorkflowEditor;
